// src/modules/app-views/invoices/pages/Document/modals/ItemModal/Step2.jsx
// *******************************************************************************
// TAX REVIEW STEP
// *******************************************************************************
// Step 2 of ItemModal - allows editing fiscal fields and validates required data.
// User can correct NCM, CFOP, tax codes and rates before confirming the item.
// Taxes are recalculated whenever any field changes.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Row, Col, Typography, Alert, Card, Divider } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import fiscalService, { isStCode, ICMS_CST_WITH_ST, ICMS_CSOSN_WITH_ST } from 'services/fiscalService';
import {
  ICMS_ORIGIN_OPTIONS,
  ICMS_CST_OPTIONS,
  ICMS_CSOSN_OPTIONS,
  PIS_COFINS_CST_OPTIONS,
  IPI_CST_OPTIONS,
  CFOP_OPTIONS,
} from 'modules/app-views/products/constants';

const { Title, Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const TaxReviewStepContent = ({
  form,
  calculatedTaxes,
  onRecalculate,
  isNfce,
  isSimples,
  hasMissingNcm,
  hasMissingCfop,
  showIcmsSt,
}) => {
  return (
    <div>
      {/* Validation Alerts */}
      {(hasMissingNcm || hasMissingCfop) && (
        <Alert
          type="error"
          title={translateX('fiscal_data_incomplete')}
          description={translateX('fiscal_data_incomplete_description')}
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {!hasMissingNcm && !hasMissingCfop && (
        <Alert
          title={translateX('step2_taxes_info')}
          description={translateX('step2_taxes_description')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Required Fiscal Fields Section */}
      <Card size="small" title={translateX('required_fiscal_data')} style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={8}>
            <SimpleInput
              name="ncm"
              label="NCM"
              placeholder="00000000"
              maxLength={8}
              required
              rules={[
                { required: true, message: translateX('ncm_required') },
                { pattern: /^\d{8}$/, message: translateX('ncm_invalid_format') },
              ]}
            />
          </Col>
          <Col xs={24} md={8}>
            <SimpleSelect
              name="cfop"
              label="CFOP"
              options={CFOP_OPTIONS}
              required
              showSearch
              rules={[{ required: true, message: translateX('cfop_required') }]}
            />
          </Col>
          <Col xs={24} md={8}>
            <SimpleInput name="cest" label="CEST" placeholder="0000000" maxLength={7} />
          </Col>
        </Row>
      </Card>

      {/* ICMS Section */}
      <Card size="small" title="ICMS" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={6}>
            <SimpleSelect name="icmsOrigin" label="origin" options={ICMS_ORIGIN_OPTIONS} />
          </Col>
          <Col xs={24} md={6}>
            {isSimples ? (
              <SimpleSelect
                name="icmsCsosn"
                label="CSOSN"
                options={ICMS_CSOSN_OPTIONS}
                onChange={onRecalculate}
              />
            ) : (
              <SimpleSelect
                name="icmsCst"
                label="CST"
                options={ICMS_CST_OPTIONS}
                onChange={onRecalculate}
              />
            )}
          </Col>
          <Col xs={12} md={6}>
            <SimpleNumber
              name="icmsRate"
              label="rate"
              min={0}
              max={100}
              decimalPlaces={3}
              percentage
              onChange={onRecalculate}
            />
          </Col>
          <Col xs={12} md={6}>
            <SimpleCurrency
              name="icmsValue"
              label="value"
              decimalPlaces={2}
              showCurrencySelect={false}
              disabled
            />
          </Col>
        </Row>
      </Card>

      {/* ICMS-ST Section - Only for NFe with ST codes */}
      {showIcmsSt && (
        <Card size="small" title="ICMS-ST" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 8]}>
            <Col xs={12} md={6}>
              <SimpleNumber
                name="icmsStMva"
                label="mva"
                tooltip="mva_tooltip"
                min={0}
                max={200}
                decimalPlaces={2}
                percentage
                onChange={onRecalculate}
              />
            </Col>
            <Col xs={12} md={6}>
              <SimpleCurrency
                name="icmsStBase"
                label="st_base"
                decimalPlaces={2}
                showCurrencySelect={false}
                disabled
              />
            </Col>
            <Col xs={12} md={6}>
              <SimpleNumber
                name="icmsStRate"
                label="st_rate"
                min={0}
                max={100}
                decimalPlaces={2}
                percentage
                onChange={onRecalculate}
              />
            </Col>
            <Col xs={12} md={6}>
              <SimpleCurrency
                name="icmsStValue"
                label="st_value"
                decimalPlaces={2}
                showCurrencySelect={false}
                disabled
              />
            </Col>
          </Row>
          <Divider style={{ margin: '12px 0' }} />
          <Row gutter={[16, 8]}>
            <Col xs={12} md={6}>
              <SimpleNumber
                name="fcpRate"
                label="fcp_rate"
                tooltip="fcp_tooltip"
                min={0}
                max={5}
                decimalPlaces={2}
                percentage
                onChange={onRecalculate}
              />
            </Col>
            <Col xs={12} md={6}>
              <SimpleCurrency
                name="fcpValue"
                label="fcp_value"
                decimalPlaces={2}
                showCurrencySelect={false}
                disabled
              />
            </Col>
            <Col xs={24} md={12}>
              <SimpleCurrency
                name="icmsStTotal"
                label="st_total"
                decimalPlaces={2}
                showCurrencySelect={false}
                disabled
              />
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        {/* PIS Section */}
        <Col xs={24} md={12}>
          <Card size="small" title="PIS">
            <Row gutter={[16, 8]}>
              <Col xs={24}>
                <SimpleSelect
                  name="pisCst"
                  label="CST"
                  options={PIS_COFINS_CST_OPTIONS}
                  onChange={onRecalculate}
                />
              </Col>
              <Col xs={12}>
                <SimpleNumber
                  name="pisRate"
                  label="rate"
                  min={0}
                  max={100}
                  decimalPlaces={3}
                  percentage
                  onChange={onRecalculate}
                />
              </Col>
              <Col xs={12}>
                <SimpleCurrency
                  name="pisValue"
                  label="value"
                  decimalPlaces={2}
                  showCurrencySelect={false}
                  disabled
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* COFINS Section */}
        <Col xs={24} md={12}>
          <Card size="small" title="COFINS">
            <Row gutter={[16, 8]}>
              <Col xs={24}>
                <SimpleSelect
                  name="cofinsCst"
                  label="CST"
                  options={PIS_COFINS_CST_OPTIONS}
                  onChange={onRecalculate}
                />
              </Col>
              <Col xs={12}>
                <SimpleNumber
                  name="cofinsRate"
                  label="rate"
                  min={0}
                  max={100}
                  decimalPlaces={3}
                  percentage
                  onChange={onRecalculate}
                />
              </Col>
              <Col xs={12}>
                <SimpleCurrency
                  name="cofinsValue"
                  label="value"
                  decimalPlaces={2}
                  showCurrencySelect={false}
                  disabled
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* IPI Section - Only for NFe, not for NFC-e */}
        {!isNfce && (
          <Col xs={24} md={12}>
            <Card size="small" title="IPI">
              <Row gutter={[16, 8]}>
                <Col xs={24}>
                  <SimpleSelect
                    name="ipiCst"
                    label="CST"
                    options={IPI_CST_OPTIONS}
                    onChange={onRecalculate}
                  />
                </Col>
                <Col xs={12}>
                  <SimpleNumber
                    name="ipiRate"
                    label="rate"
                    min={0}
                    max={100}
                    decimalPlaces={3}
                    percentage
                    onChange={onRecalculate}
                  />
                </Col>
                <Col xs={12}>
                  <SimpleCurrency
                    name="ipiValue"
                    label="value"
                    decimalPlaces={2}
                    showCurrencySelect={false}
                    disabled
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        )}

        {/* Total Taxes Card */}
        <Col xs={24} md={isNfce ? 24 : 12}>
          <Card
            size="small"
            style={{ background: '#f6ffed', borderColor: '#b7eb8f', height: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Text strong>{translateX('total_taxes')}:</Text>
              <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                {financialService.formatCurrency(calculatedTaxes?.totalTaxes || 0)}
              </Title>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const taxReviewStep = ({
  calculatedTaxes,
  setCalculatedTaxes,
  fiscalConfig,
  documentType,
  isNfce,
}) => ({
  stepTitle: 'taxes',
  stepTooltip: 'step2_tooltip',
  validationFields: ['ncm', 'cfop'],
  content: ({ form }) => {
    // Determine tax regime
    const taxRegime = fiscalConfig?.taxRegime || 'simples_nacional';
    const isSimples =
      typeof taxRegime === 'string' ? taxRegime.includes('simples') : [1, 2, 4].includes(taxRegime);

    // Check for missing required fields
    const ncmValue = form?.getFieldValue('ncm');
    const cfopValue = form?.getFieldValue('cfop');
    const hasMissingNcm = !ncmValue;
    const hasMissingCfop = !cfopValue;

    // Determine if ICMS-ST should be shown (NFe only + ST codes)
    const currentCst = form?.getFieldValue('icmsCst');
    const currentCsosn = form?.getFieldValue('icmsCsosn');
    const hasStCode = isSimples
      ? ICMS_CSOSN_WITH_ST.includes(currentCsosn)
      : ICMS_CST_WITH_ST.includes(currentCst);
    const showIcmsSt = !isNfce && hasStCode;

    // Recalculate taxes when any rate/CST field changes
    const handleRecalculate = () => {
      if (!form) return;

      const quantity = form.getFieldValue('quantity') || 1;
      const unitValue = form.getFieldValue('unitValue') || 0;
      const discount = form.getFieldValue('discount') || { init: 'percentage', percentage: 0, value: 0 };

      // Calculate subtotal and apply discount value
      const subtotal = financialService.roundCurrency(quantity * unitValue);
      const discountValue = discount.value || 0;
      const totalValue = financialService.roundCurrency(Math.max(0, subtotal - discountValue));

      // Get ICMS-ST parameters from form
      const icmsStMva = form.getFieldValue('icmsStMva') ?? 0;
      const icmsStRate = form.getFieldValue('icmsStRate') ?? 0;
      const fcpRate = form.getFieldValue('fcpRate') ?? 0;
      const ipiValue = form.getFieldValue('ipiValue') ?? 0;

      // Get fiscal data from form
      const productFiscal = {
        icmsOrigin: form.getFieldValue('icmsOrigin') ?? 0,
        icmsCst: form.getFieldValue('icmsCst'),
        icmsCsosn: form.getFieldValue('icmsCsosn'),
        icmsRate: form.getFieldValue('icmsRate') ?? 0,
        pisCst: form.getFieldValue('pisCst'),
        pisRate: form.getFieldValue('pisRate') ?? 0,
        cofinsCst: form.getFieldValue('cofinsCst'),
        cofinsRate: form.getFieldValue('cofinsRate') ?? 0,
        ipiCst: form.getFieldValue('ipiCst'),
        ipiRate: form.getFieldValue('ipiRate') ?? 0,
        // ICMS-ST parameters
        icmsStMva,
        icmsStRate,
        fcpRate,
      };

      // Calculate taxes (includes ST for NFe with ST codes)
      const taxes = fiscalService.calculateItemTaxes({
        totalValue,
        fiscalConfig,
        productFiscal,
        documentType: documentType || 'nfe',
        ipiValue, // Pass IPI value for ST base calculation
      });

      setCalculatedTaxes(taxes);

      // Update form with calculated values
      const formUpdates = {
        icmsValue: taxes.icms?.value || 0,
        pisValue: taxes.pis?.value || 0,
        cofinsValue: taxes.cofins?.value || 0,
        ipiValue: taxes.ipi?.value || 0,
      };

      // Add ICMS-ST values if applicable
      if (taxes.icmsSt) {
        formUpdates.icmsStBase = taxes.icmsSt.baseValue || 0;
        formUpdates.icmsStValue = taxes.icmsSt.value || 0;
        formUpdates.fcpValue = taxes.icmsSt.fcpValue || 0;
        formUpdates.icmsStTotal = taxes.icmsSt.totalValue || 0;
      }

      form.setFieldsValue(formUpdates);
    };

    return (
      <TaxReviewStepContent
        form={form}
        calculatedTaxes={calculatedTaxes}
        onRecalculate={handleRecalculate}
        isNfce={isNfce}
        isSimples={isSimples}
        hasMissingNcm={hasMissingNcm}
        hasMissingCfop={hasMissingCfop}
        showIcmsSt={showIcmsSt}
      />
    );
  },
  onNext: ({ form }) => {
    // Validate required fields before advancing to Step 3
    if (!form) return false;

    const ncmValue = form.getFieldValue('ncm');
    const cfopValue = form.getFieldValue('cfop');

    // Block if NCM is missing
    if (!ncmValue) {
      form.setFields([{ name: 'ncm', errors: [translateX('ncm_required')] }]);
      return false;
    }

    // Validate NCM format (8 digits)
    if (!/^\d{8}$/.test(ncmValue)) {
      form.setFields([{ name: 'ncm', errors: [translateX('ncm_invalid_format')] }]);
      return false;
    }

    // Block if CFOP is missing
    if (!cfopValue) {
      form.setFields([{ name: 'cfop', errors: [translateX('cfop_required')] }]);
      return false;
    }

    return true; // Allow advancing to Step 3
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default taxReviewStep;
